import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../Global/Layout';
import DataTable from '../../Global/DataTable';
import { showSuccess, showError, showConfirm, showAlert, showWarning } from '../../../utils/sweetAlertConfig';
import { API_CONFIG, LOTTO_TYPE } from '../../../contexts/constants';
import { LottoBallsDisplay } from '../../../utils/LottoBalls';
import { formatTHB } from '../../../utils/numberFormat';
import { formatDateTimeTable } from '../../../utils/dateFormatter';

const PayWaitingCheck = ({ onLogout, userRole }) => {
	const [payments, setPayments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	const [selectedPayment, setSelectedPayment] = useState(null);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [orderSummary, setOrderSummary] = useState({
		total_with_approve: 0,
		total_order_ball: 0,
		sum_price: 0
	});

	const fetchPendingPayments = useCallback(async () => {
		try {
			setLoading(true);
			const params = new URLSearchParams();
			params.append('page', currentPage);
			params.append('pageSize', pageSize);

			const response = await axios.get(`${API_CONFIG.BASE_URL}/orders/pending/payments?${params.toString()}`, {
				headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
			});

			if (response.data.success) {
				setPayments(response.data.data);
				setTotalItems(response.data.total || 0);
			}
		} catch (error) {
			console.error('Error fetching pending payments:', error);
			showError('ไม่สามารถโหลดข้อมูลได้');
		} finally {
			setLoading(false);
		}
	}, [currentPage, pageSize]);

	const fetchDataSummary = useCallback(async () => {
		if(userRole !== 'customer'){
			try {
				const response = await axios.get(`${API_CONFIG.BASE_URL}/report/order-waiting-approve`, {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});

				setOrderSummary({
					total_with_approve: response.data.total_with_approve,
					total_order_ball: response.data.total_order_ball,
					sum_price: response.data.sum_price
				});
			} catch (error) {
				console.error('Error fetching results:', error);
			}
		}
	}, [userRole]);

	useEffect(() => {
		fetchPendingPayments();
		fetchDataSummary();
	}, [currentPage, pageSize, fetchPendingPayments, fetchDataSummary]);

	if (!['sysadmin', 'owner', 'admin'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

	const handleApprove = async (payment) => {
		const result = await showConfirm({
			title: 'ยืนยันการอนุมัติ',
			html: `
				<p class="mb-0">คุณต้องการอนุมัติvการชำระเงิน</p>
				<p class="mb-0">เลขที่คำสั่งซื้อ <strong>${payment.orderCode}</strong></p>
				<p class="mb-0">ของ <strong>${payment.customerName}</strong> จำนวนเงิน <strong>${formatTHB(payment.totalAmount)} บาท ใช่หรือไม่?</strong></p>
			`,
			confirmText: 'อนุมัติ',
			cancelText: 'ยกเลิก',
			confirmVariant: 'success'
		});

		if (result.isConfirmed) {
			try {
				const response = await axios.put(
					`${API_CONFIG.BASE_URL}/orders/pending/${payment.billId}/approve`,
					{},
					{
						headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
					}
				);

				if (response.data.success) {
					showSuccess('อนุมัติการชำระเงินเรียบร้อยแล้ว');
					fetchPendingPayments();
					fetchDataSummary();
				}
			} catch (error) {
				console.error('Error approving payment:', error);
				showError('ไม่สามารถอนุมัติการชำระเงินได้');
			}
		}
	};

	const handleReject = async (payment) => {
		const result = await showAlert({
			title: 'ปฏิเสธการชำระเงิน',
			html: `
				<p class="mb-0">คุณต้องการปฏิเสธการชำระเงิน</p>
				<p class="mb-0">เลขที่คำสั่งซื้อ <strong>${payment.orderCode}</strong></p>
				<p class="mb-3">ของ <strong>${payment.customerName}</strong> ใช่หรือไม่?</p>
				<textarea id="reject-reason" class="swal2-input" placeholder="กรุณาระบุเหตุผล" style="height: 70px;width: -webkit-fill-available;border: 2px solid #5c5c5c;border-radius: 5px;margin-left: 2rem;margin-right: 2rem;"></textarea>
			`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'ปฏิเสธ',
			cancelButtonText: 'ยกเลิก',
			confirmVariant: 'danger',
			preConfirm: () => {
				return document.getElementById('reject-reason').value;
			}
		});

		if (result.isConfirmed) {
			if(result.value){
				try {
					const response = await axios.put(
						`${API_CONFIG.BASE_URL}/orders/pending/${payment.billId}/reject`,
						{ reason: result.value },
						{
							headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
						}
					);

					if (response.data.success) {
						showSuccess('ปฏิเสธการชำระเงินเรียบร้อยแล้ว');
						fetchPendingPayments();
						fetchDataSummary();
					}
				} catch (error) {
					console.error('Error rejecting payment:', error);
					showError('ไม่สามารถปฏิเสธการชำระเงินได้');
				}
			}else{
				showWarning('แจ้งเตือน', 'กรุณาระบุเหตุผลในการปฏิเสธการชำระเงิน');
			}
		}
	};

	const handleViewDetail = (payment) => {
		setSelectedPayment(payment);
		setShowDetailModal(true);
	};

	const formatDate = (dateString) => {
		return formatDateTimeTable(dateString);
	};

	const formatTickets = (tickets) => {
		return tickets.map((ticket, index) => (
			<div key={index} className="flex items-center gap-2 mb-1">
				<span className="text-xs text-gray-500 dark:text-gray-400">#{index + 1}</span>
				<LottoBallsDisplay grayBalls={ticket.whiteBalls} specialBall={ticket.powerBall} type={ticket.type} />
			</div>
		)).slice(0, 2); // Show only first 2 tickets in table
	};

	const columns = [
		{
			key: 'purchaseDate',
			header: 'เวลาที่ซื้อ',
			render: (row) => (
				<div>
					{formatDate(row.purchaseDate)}
					{row.orderCode && (
						<span className="text-xs text-gray-500 dark:text-gray-400">
							<br/>
							{row.orderCode}
						</span>
					)}
				</div>
			)
		},
		{
			key: 'customerName',
			header: 'ชื่อลูกค้า',
			render: (row) => (
				<div>
					{row.customerName}
					{row.agentName && (
						<span className="text-xs text-gray-500 dark:text-gray-400">
							<br/>
							(ตัวแทน: {row.agentName})
						</span>
					)}
				</div>
			)
		},
		{
			key: 'lotteryType',
			header: 'ประเภท',
			render: (row) => (
				<div className="text-center">
					{row.lotteryType === 'mixed' ? (
						<div className="space-y-1">
							<span className="inline-block px-3 py-1 rounded-full text-xs font-semibold min-w-32 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
								{LOTTO_TYPE.powerball}
							</span>
							<br/>
							<span className="inline-block px-3 py-1 rounded-full text-xs font-semibold min-w-32 bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
								{LOTTO_TYPE.megamillions}
							</span>
						</div>
					) : (
						<span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold min-w-32 ${
							row.lotteryType === 1
							? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
							: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
						}`}>
							{row.lotteryType === 1 ? LOTTO_TYPE.powerball : LOTTO_TYPE.megamillions}
						</span>
					)}
				</div>
			)
		},
		{
			key: 'tickets',
			header: 'เลขที่ซื้อ',
			render: (row) => (
				<div className="text-sm">
					{formatTickets(row.tickets)}
					{row.ticketCount > 2 && (
						<button
							onClick={() => handleViewDetail(row)}
							className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
						>
							+ ดูทั้งหมด ({row.ticketCount} ใบ)
						</button>
					)}
				</div>
			)
		},
		{
			key: 'totalAmount',
			header: 'ยอดเงิน (บาท)',
			render: (row) => (
				<div className="text-center">
					<div className="font-semibold text-green-600 dark:text-green-400">
						{formatTHB(row.totalAmount || 0)}
					</div>
					{row.totalDiscount ? <div className="font-semibold text-red-600 dark:text-red-400">
						ส่วนลด {formatTHB(row.totalDiscount)}
					</div> : null}
				</div>
			)
		},
		{
			key: 'actions',
			header: 'จัดการ',
			render: (row) => (
				<div className="flex gap-2">
					<button
						onClick={() => handleViewDetail(row)}
						className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition"
					>
						ดูสลิป
					</button>
					<button
						onClick={() => handleApprove(row)}
						className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition"
					>
						อนุมัติ
					</button>
					<button
						onClick={() => handleReject(row)}
						className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
					>
						ปฏิเสธ
					</button>
				</div>
			)
		}
	];

	return (
		<Layout title="ตรวจสอบ/อนุมัติการชำระเงิน" onLogout={onLogout} userRole={userRole}>
			{/* Header */}
			<div className="flex items-center justify-between mb-4 ">
				<div>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
						รายการรออนุมัติ ที่ลูกค้าชำระเงินแล้ว
					</p>
				</div>
				<button
					onClick={() => {fetchPendingPayments(); fetchDataSummary();}}
					disabled={loading}
					className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition font-medium flex items-center gap-2"
				>
					<svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
					</svg>
					รีเฟรช
				</button>
			</div>

			{/* Stats */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
				<div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
					<div className="flex items-center gap-3">
						<div className="p-3 bg-blue-500 rounded-lg">
							<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						</div>
						<div>
							<p className="text-sm text-gray-600 dark:text-gray-400">คำสั่งซื้อที่รอตรวจสอบ</p>
							<p className="text-2xl font-bold text-gray-900 dark:text-white">{formatTHB(orderSummary.total_with_approve || 0)}</p>
						</div>
					</div>
				</div>

				<div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-700 rounded-xl p-4">
					<div className="flex items-center gap-3">
						<div className="p-3 bg-green-500 rounded-lg">
							<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
							</svg>
						</div>
						<div>
							<p className="text-sm text-gray-600 dark:text-gray-400">จำนวนใบรวม</p>
							<p className="text-2xl font-bold text-gray-900 dark:text-white">{formatTHB(orderSummary.total_order_ball || 0)}</p>
						</div>
					</div>
				</div>

				<div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4">
					<div className="flex items-center gap-3">
						<div className="p-3 bg-yellow-500 rounded-lg">
							<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						</div>
						<div>
							<p className="text-sm text-gray-600 dark:text-gray-400">ยอดเงินรวม</p>
							<div className='flex items-center gap-2'>
								<p className="text-2xl font-bold text-gray-900 dark:text-white">{formatTHB(orderSummary.sum_price || 0)}</p>
								<p className="text-sm text-gray-600 dark:text-gray-400">(รายการที่รออนุมัติ และหักส่วนลดแล้ว)</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={payments}
				loading={loading}
				page={currentPage}
				pageSize={pageSize}
				totalItems={totalItems}
				onPageChange={setCurrentPage}
				onPageSizeChange={setPageSize}
			/>

			{/* Detail Modal */}
			{showDetailModal && selectedPayment && (
				<div className="fixed top-0 inset-x-0 bg-black/50 flex items-top justify-center z-50 p-4 max-h-screen overflow-y-auto flex align-middle">
					<div className="flex align-middle h-auto my-auto">
						<div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl h-full w-full max-w-full sm:max-w-lg min-w-full sm:min-w-[800px] my-5">
							<div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
								<h3 className="text-xl font-bold text-gray-900 dark:text-white">รายละเอียดการชำระเงิน</h3>
								<button
									onClick={() => setShowDetailModal(false)}
									className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
								>
									<svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							{/* Modal Content */}
							<div className="p-6 space-y-4">
								{/* Customer Info */}
								<div className="grid grid-cols-2 gap-4">
									<div>
										<p className="text-sm text-gray-500 dark:text-gray-400">ชื่อลูกค้า</p>
										<p className="text-lg font-semibold text-gray-900 dark:text-white">{selectedPayment.customerName}</p>
									</div>
									<div>
										<p className="text-sm text-gray-500 dark:text-gray-400">เวลาที่ซื้อ</p>
										<p className="text-lg font-semibold text-gray-900 dark:text-white">{formatDate(selectedPayment.purchaseDate)}</p>
									</div>
									<div>
										<p className="text-sm text-gray-500 dark:text-gray-400">ชื่อตัวแทน</p>
										<p className="text-lg font-semibold text-gray-900 dark:text-white">{selectedPayment.agentName}</p>
									</div>
									<div>
										<p className="text-sm text-gray-500 dark:text-gray-400"></p>
										<p className="text-lg font-semibold text-gray-900 dark:text-white"></p>
									</div>
									<div>
										<p className="text-sm text-gray-500 dark:text-gray-400">ประเภท</p>
										<p className="text-lg font-semibold text-gray-900 dark:text-white">
											{selectedPayment.lotteryType === 'mixed' ? (
												<>
													<span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 mr-2">
														{LOTTO_TYPE.powerball}
													</span>
													<span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
														{LOTTO_TYPE.megamillions}
													</span>
												</>
											) : (
												selectedPayment.lotteryType === 1 ? LOTTO_TYPE.powerball : LOTTO_TYPE.megamillions
											)}
										</p>
									</div>
									<div>
										<p className="text-sm text-gray-500 dark:text-gray-400">ยอดเงิน</p>
										<p className="text-lg font-semibold text-green-600 dark:text-green-400">
											{(selectedPayment.totalAmount || 0).toLocaleString()} บาท
										</p>
									</div>
								</div>
							{/* Discount Info */}
							{selectedPayment.discounts && selectedPayment.discounts.length > 0 && (
								<div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
									<h4 className="text-sm font-semibold text-orange-700 dark:text-orange-300 mb-3 flex items-center gap-2">
										<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										ส่วนลดที่ใช้
									</h4>
									<div className="space-y-2">
										{selectedPayment.discounts.map((discount, index) => (
											<div key={index} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
												<div className="flex items-center gap-3">
													<span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
														discount.lotType === 1
															? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
															: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
													}`}>
														{discount.lotType === 1 ? LOTTO_TYPE.powerball : LOTTO_TYPE.megamillions}
													</span>
													<div>
														<p className="text-sm font-medium text-gray-700 dark:text-gray-300">
															{discount.discountType === 1 ? 'ส่วนลดลูกค้าใหม่' : 'ส่วนลดตามยอดสะสม'}
														</p>
														{discount.ballFree > 0 && (
															<p className="text-xs text-gray-500 dark:text-gray-400">
																ซื้อ {discount.ballCount} ใบ แถม {discount.ballFree} ใบ
															</p>
														)}
													</div>
												</div>
												<div className="text-right">
													<p className="text-lg font-bold text-orange-600 dark:text-orange-400">
														-{formatTHB(discount.priceDiscount)} บาท
													</p>
												</div>
											</div>
										))}
									</div>
									{/* {selectedPayment.totalDiscount && (
										<div className="mt-3 pt-3 border-t border-orange-200 dark:border-orange-800 flex justify-between items-center">
											<p className="text-sm font-semibold text-gray-700 dark:text-gray-300">รวมส่วนลดทั้งหมด</p>
											<p className="text-xl font-bold text-orange-600 dark:text-orange-400">
												-{formatTHB(selectedPayment.totalDiscount)} บาท
											</p>
										</div>
									)} */}
								</div>
							)}
								{/* Payment Slip */}
								<div>
									<h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">หลักฐานการชำระเงิน</h4>
									<div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
										<img
											src={selectedPayment.slipImage || '/slip-bank-empty.jpg'}
											alt="Payment Slip"
											style={{ height: '400px' }}
											className="max-w-md mx-auto rounded-lg shadow-lg"
										/>
									</div>
								</div>

								{/* Tickets */}
								<div>
									<h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
										เลขที่ซื้อ ({selectedPayment.ticketCount} ใบ)
									</h4>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
										{selectedPayment.tickets.map((ticket, index) => (
											<div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
												<div className="flex items-center justify-between mb-2">
													<span className="text-xs font-semibold text-gray-600 dark:text-gray-400">ใบที่ {index + 1}</span>
													<span className={`text-xs px-2 py-0.5 rounded ${
														ticket.type === 'powerball'
															? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
															: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
													}`}>
														{LOTTO_TYPE[ticket.type]}
													</span>
												</div>
												<div className="flex items-center gap-2">
													<LottoBallsDisplay grayBalls={ticket.whiteBalls} specialBall={ticket.powerBall} type={ticket.type} />
												</div>
											</div>
										))}
									</div>
								</div>

								{/* Actions */}
								<div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
									<button
										onClick={() => setShowDetailModal(false)}
										className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium"
									>
										ปิด
									</button>
									<button
										onClick={() => {
											setShowDetailModal(false);
											handleReject(selectedPayment);
										}}
										className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium"
									>
										ปฏิเสธ
									</button>
									<button
										onClick={() => {
											setShowDetailModal(false);
											handleApprove(selectedPayment);
										}}
										className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition font-medium"
									>
										อนุมัติ
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</Layout>
	);
};

export default PayWaitingCheck;
